import { DialogActions, ErrorMessage } from '@/components/Elements';
import { InputField, ToggleField } from '@/components/Form';
import { useArticleSearchCondition } from '@/features/analyze/hooks/useArticleSearchCondition';
import { useSavedFiltersStatus } from '@/features/analyze/hooks/useSavedFiltersStatus';
import { graphql, readFragment, useMutation } from '@/lib/graphql';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCaution } from './AlertCaution';
import { SavedFilterItem } from './SavedFilterItem';

// フィルター保存用のMutation
const createSavedFilterMutation = graphql(
  `
    mutation createSavedFilter($input: CreateSavedFilterInput!) {
      createSavedFilter(input: $input) {
        success
        userErrors {
          message
          code
        }
        savedFilter {
          ...SavedFilterItemFragment
        }
        affectedSavedFilters {
          ...SavedFilterItemFragment
        }
      }
    }
  `,
  [SavedFilterItem.fragments.data]
);

interface CreateFilterModeProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateFilterMode = ({
  onClose,
  onSuccess,
}: CreateFilterModeProps) => {
  const { t } = useTranslation();
  const [filterName, setFilterName] = useState('');
  const [alertEnabled, setAlertEnabled] = useState(false);

  const condition = useArticleSearchCondition();

  const { isFull: isSavedFiltersFull } = useSavedFiltersStatus();

  const [createSavedFilter, { data, loading }] = useMutation(
    createSavedFilterMutation,
    {
      update: (cache, { data }) => {
        const createResult = data?.createSavedFilter;
        if (!createResult?.success || !createResult?.savedFilter) return;

        const newFilter = readFragment(
          SavedFilterItem.fragments.data,
          createResult.savedFilter
        );

        cache.modify({
          fields: {
            savedFilters: (existing = [], { toReference }) => [
              ...existing,
              toReference(newFilter),
            ],
          },
        });

        createResult.affectedSavedFilters?.forEach((affectedFilterRef) => {
          const affectedFilter = readFragment(
            SavedFilterItem.fragments.data,
            affectedFilterRef
          );
          if (!affectedFilter) return;

          cache.writeFragment({
            id: cache.identify({
              __typename: 'SavedFilter',
              id: affectedFilter.id,
            }),
            fragment: SavedFilterItem.fragments.data,
            data: affectedFilter,
          });
        });
      },
    }
  );

  const handleSave = async () => {
    // 早期return: フィルター名が空の場合は何もしない
    if (!filterName.trim()) {
      return;
    }

    const result = await createSavedFilter({
      variables: {
        input: {
          name: filterName.trim(),
          alertEnabled,
          condition,
        },
      },
    });

    if (result.data?.createSavedFilter?.success) {
      onSuccess();
    }
  };

  const canSave = !isSavedFiltersFull && filterName.trim() !== '';

  return (
    <div className="space-y-6">
      <InputField
        label={t('フィルタ名')}
        value={filterName}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setFilterName(e.target.value)
        }
        placeholder={t('フィルタ名を入力してください')}
        className="border-rw-sub"
      />

      <div className="space-y-4">
        <ToggleField
          label={t('通知を受け取る')}
          checked={alertEnabled}
          onToggle={(checked) => setAlertEnabled(checked)}
          size="sm"
        />
        <div
          className={clsx(
            'overflow-hidden transition-all duration-500 ease-in-out',
            alertEnabled ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          )}
        >
          <AlertCaution />
        </div>
      </div>

      {/* エラーメッセージ */}
      <ErrorMessage errors={data?.createSavedFilter?.userErrors} />

      {/* アクションボタン（下部） */}
      <DialogActions
        onCancel={onClose}
        onConfirm={handleSave}
        confirmText={t('保存')}
        isConfirmDisabled={!canSave}
        isConfirmLoading={loading}
      />
    </div>
  );
};
